import axios from 'axios';
import i18n from '../i18n';

const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production' 
    ? '/api'  // Cloudflare Pages will serve from same domain
    : 'http://localhost:8787/api'  // Wrangler dev server
);

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  // On mettra withCredentials seulement pour les endpoints d'auth
  withCredentials: false,
  maxRedirects: 0,
});

// Request interceptor for logging and language
api.interceptors.request.use(
  (config) => {
    console.log(`Making API request: ${config.method?.toUpperCase()} ${config.url}`);
    
    // Add language to requests
    const currentLanguage = i18n.language || 'en';
    config.headers['Accept-Language'] = currentLanguage;
    config.params = { ...config.params, lang: currentLanguage };
    
    return config;
  },
  (error) => {
    console.error('API request error:', error);
    return Promise.reject(error);
  }
);

// Simple response interceptor for error logging only
api.interceptors.response.use(
  (response) => {
    console.log('API response success:', response.config.url, response.status);
    return response;
  },
  (error) => {
    console.error('API response error for URL:', error.config?.url, error);
    
    if (error.response) {
      console.error('Error data:', error.response.data);
      console.error('Error status:', error.response.status);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error message:', error.message);
    }
    return Promise.reject(error);
  }
);

// API functions
export const fetchRootNodes = async () => {
  try {
    const response = await api.get('/tree/root');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch root nodes:', error);
    throw new Error('Failed to load root categories');
  }
};

export const fetchCompleteTree = async () => {
  try {
    console.log('About to fetch complete tree...');
    const response = await api.get('/tree/complete');
    console.log('Complete tree response received:', response.status);
    console.log('Complete tree data received:', response.data.length, 'nodes');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch complete tree:', error);
    console.error('Error details:', error.response, error.message, error.stack);
    throw new Error('Failed to load complete tree');
  }
};

export const fetchNodeById = async (id, includeChildren = false) => {
  try {
    const response = await api.get(`/tree/node/${id}`, {
      params: { includeChildren }
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch node ${id}:`, error);
    throw new Error('Failed to load node details');
  }
};

export const fetchNodeChildren = async (parentId) => {
  try {
    const response = await api.get(`/tree/children/${parentId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch children for node ${parentId}:`, error);
    throw new Error('Failed to load child nodes');
  }
};

export const fetchNodesByLevel = async (level) => {
  try {
    const response = await api.get(`/tree/level/${level}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch nodes at level ${level}:`, error);
    throw new Error('Failed to load nodes at this level');
  }
};

export const fetchTreeNavigation = async (nodeId, contextLevels = 3) => {
  try {
    const response = await api.get(`/tree/navigation/${nodeId}`, {
      params: { contextLevels }
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch navigation for node ${nodeId}:`, error);
    throw new Error('Failed to load navigation data');
  }
};

export const createNode = async (nodeData) => {
  try {
    const response = await api.post('/tree/node', nodeData);
    return response.data;
  } catch (error) {
    console.error('Failed to create node:', error);
    throw new Error('Failed to create new node');
  }
};

export const healthCheck = async () => {
  try {
    const response = await api.get('/tree/health');
    return response.data;
  } catch (error) {
    console.error('Health check failed:', error);
    throw new Error('API health check failed');
  }
};

/**
 * Get PayPal SDK URL and Button ID based on environment
 */
export const getPayPalConfig = async () => {
  try {
    const response = await api.get('/config/paypal-url');
    return {
      sdkUrl: response.data.sdkUrl,
      buttonId: response.data.buttonId,
      environment: response.data.environment
    };
  } catch (error) {
    console.error('Failed to fetch PayPal config:', error);
    // Fallback to production values
    return {
      sdkUrl: 'https://www.paypal.com/sdk/js?client-id=YOUR_CLIENT_ID&components=hosted-buttons',
      buttonId: 'DFW26B7N7KXYY',
      environment: 'fallback'
    };
  }
};

export default api;
